"use client";

import { filterDiff } from "@/lib/listing-signals";
import { useHiddenIds } from "@/lib/hidden-listings";
import StateAuctionsNote from "@/components/auctions/StateAuctionsNote";
import { placeReplacesRadius } from "@/lib/place-vs-radius";
import { useFeatureFlag } from "@/lib/features";
import { Suspense, useCallback, useMemo, useState, useEffect, useLayoutEffect, useRef, type ReactNode } from "react";
import { useSearchParams, useRouter } from "next/navigation";
import type { ReadonlyURLSearchParams } from "next/navigation";
import { flushSync } from "react-dom";
import { haptic } from "@/lib/haptics";
import { Map as MapIcon, X, SearchX, UserRound, Plus, Loader2, ArrowUp } from "lucide-react";
import { captureEvent } from "@/lib/tracking";
import Link from "@/lib/localized-link";
import SharedHeader from "@/components/SharedHeader";
import ListingsFilterBar, { FiltersButton } from "@/components/listings/ListingsFilterBar";
import AppliedFilterChips from "@/components/listings/AppliedFilterChips";
import ZeroResultsHint from "@/components/listings/ZeroResultsHint";
import { askedFacts } from "@/lib/text-facts";
import MagicSearchBar from "@/components/listings/MagicSearchBar";
import ListingsFilterPanel from "@/components/listings/ListingsFilterPanel";
import ListingsSortPicker from "@/components/listings/ListingsSortPicker";
import SavedFilters from "@/components/listings/SavedFilters";
import CreateAlertButton from "@/components/listings/CreateAlertButton";
import DealAlertNudge, { countListingOpen } from "@/components/listings/DealAlertNudge";
import { fetchListings, fetchListingDetail } from "@/lib/api";
import { apiParamsFromSearch, seedKeyFor } from "@/lib/listing-search-params";
import { countActiveFilters } from "@/lib/active-filter-count";
import { FIRST_PARTY_LISTINGS } from "@/lib/flags";
import { trackEvent } from "@/lib/tracking";
import { useLocale, useT } from "@/lib/locale-context";
import { useAuth } from "@/lib/auth-context";
import { usePersonal } from "@/lib/use-personal";
import { listingPath } from "@/lib/seo/paths";
import type { ListingCard, ListingsResponse } from "@/types";
import ListingBrowseCard from "./ListingBrowseCard";
import ListingDetailPanel from "./ListingDetailPanel";
import NearbyDeals from "./NearbyDeals";
import { SkeletonCard } from "./SkeletonCard";
import { nearbyQueryFromSearch } from "@/lib/nearby-deals";
import { isSeparate } from "@/lib/property-kinds";

const PAGE_SIZE = 24;

/* ── Grid ────────────────────────────────────────────────────────────
 * Fetches a page of listings (client-side) and renders the cards +
 * detail drawer. The PARENT shell (ListingsContent, below) owns the
 * filters / sort / pagination chrome and the URL; this component only
 * cares about "given these params + page, show the cards".
 *
 * SSR seeding: when `initialData` is provided AND it represents the same
 * query this render would otherwise fetch (the default first page), the
 * grid renders those cards on its FIRST render instead of starting empty
 * + firing the useEffect. That makes the server-rendered HTML carry real
 * listing cards (the SEO win). On any later filter/page change - or when
 * initialData is absent - it fetches client-side exactly as before. */
interface GridProps {
  searchParams: ReadonlyURLSearchParams;
  page: number;
  onTotalChange?: (total: number, pages: number) => void;
  /** Server-fetched first page. Only consumed when the current render is
   *  the matching default first page (see `seedApplies`). Null disables
   *  seeding (graceful fallback to client-fetch). */
  initialData?: ListingsResponse | null;
  /** Apply a filter tapped on a card chip (row variant). */
  onFilter?: (params: Record<string, string | string[] | null>) => void;
  /** Card layout: "grid" (photo cards, default) or "list" (horizontal
   *  rows). Owned by the shell (toolbar toggle), persisted there. */
  view?: "grid" | "list";
  /** The URL's own params, when `searchParams` also carries filters a
   *  landing page pre-applied. The seed check reads these: the server
   *  fetched page 1 of the landing's filters, which is valid exactly
   *  while the URL adds nothing to them. */
  seedParams?: ReadonlyURLSearchParams;
  /** seedKeyFor() of the query the server pre-fetched (listings page). */
  seedKey?: string;
  /** A landing page's place: cards stop repeating its name. */
  hidePlace?: string | null;
  /** Infinite scroll (owner, 2026-10-13): pages append as the reader
   *  scrolls, the URL's ?page= follows the page in view, and a refresh
   *  starts at that page with "show earlier" above it. */
  infinite?: boolean;
  /** A listing to open in the drawer, as a tap on its card does: the
   *  search bar's answer to a pasted link. `seq` re-opens the same one. */
  openRequest?: { card: ListingCard; seq: number } | null;
}

/** How far ahead of the reader the next page attaches. */
const AHEAD_PX = 1600;

/** The card the reader is looking at: the given one when it is on
 *  screen, else the first card whose top crosses 40% of the viewport. */
function readingCard(prefer: string | null): { id: string; top: number } | null {
  const vh = window.innerHeight;
  if (prefer) {
    const el = document.querySelector<HTMLElement>(`[data-listing-card="${CSS.escape(prefer)}"]`);
    const top = el?.getBoundingClientRect().top;
    if (el && top != null && top > -el.offsetHeight && top < vh) return { id: prefer, top };
  }
  let best: { id: string; top: number } | null = null;
  for (const el of document.querySelectorAll<HTMLElement>("[data-listing-card]")) {
    const r = el.getBoundingClientRect();
    if (r.bottom < vh * 0.4) continue;
    if (r.top > vh) break;
    best = { id: el.dataset.listingCard!, top: r.top };
    break;
  }
  return best;
}

/** The query without the keys that only say where the reader is. */
function filterKeyOf(sp: ReadonlyURLSearchParams): string {
  const q = new URLSearchParams(sp.toString());
  q.delete("page");
  q.delete("open");
  return q.toString();
}

type Block = { page: number; results: ListingCard[] };

/**
 * True when the current (searchParams, page) pair is exactly the
 * default, unfiltered first page that the server pre-fetched: page 1,
 * no filter params set, sort newest-or-absent. Only then is it safe to
 * seed from `initialData` - both for correctness (we'd otherwise show
 * the wrong page's cards) and for hydration safety (the server rendered
 * with empty search params + page 1, so the client's first render must
 * match that exact state to reuse the same HTML).
 */
function seedApplies(searchParams: ReadonlyURLSearchParams, page: number): boolean {
  if (page !== 1) return false;
  for (const key of Array.from(searchParams.keys())) {
    // sort is allowed only if it's the default "newest".
    if (key === "sort") {
      if (searchParams.get("sort") !== "newest") return false;
      continue;
    }
    // Any other param (a real filter, or an explicit page) means this is
    // no longer the default first page the server fetched.
    return false;
  }
  return true;
}

function ListingsGrid({ searchParams, page, onTotalChange, initialData, view = "grid", onFilter, seedParams, seedKey, hidePlace = null, infinite = false, openRequest = null }: GridProps) {
  const hiddenIds = useHiddenIds();
  const t = useT();
  const { locale } = useLocale();

  // Whether the SSR-seeded payload is valid for THIS render. Computed
  // identically on server + client first render (pure fn of the same
  // inputs), so it can drive the initial state without a hydration
  // mismatch.
  // A filtered first page is seeded too when the server fetched exactly
  // this query (seedKey); otherwise only the default unfiltered page is.
  const canSeed = Boolean(initialData) && (
    seedKey
      ? page === 1 && seedKeyFor(apiParamsFromSearch(searchParams)) === seedKey
      : seedApplies(seedParams ?? searchParams, page)
  );

  const [data, setData] = useState<ListingsResponse | null>(canSeed ? initialData! : null);
  // When seeded we are NOT loading on the first render - the cards are
  // already here. This is what removes the empty/skeleton grid from the
  // crawler-visible HTML.
  const [loading, setLoading] = useState(!canSeed);
  const [error, setError] = useState<string | null>(null);
  // Skip the very first effect fetch when we already have the matching
  // server payload (avoids an immediate redundant round-trip on load).
  const skipFirstFetch = useRef(canSeed);

  // ── Infinite scroll state ──
  // The loaded pages, contiguous (first..last). `data` keeps the first
  // response for the total and the empty / error branches.
  const [blocks, setBlocks] = useState<Block[]>(canSeed ? [{ page: 1, results: initialData!.results }] : []);
  const [more, setMore] = useState<"next" | "prev" | null>(null);
  const [showTop, setShowTop] = useState(false);
  const [moreError, setMoreError] = useState(false);
  const filterKey = filterKeyOf(searchParams);
  // The URL page at the time a new search starts (later URL writes are
  // our own and must not refetch).
  const pageRef = useRef(page);
  pageRef.current = page;
  // Bumped per search; a late answer for an older search is dropped.
  const gen = useRef(0);
  const busy = useRef(false);
  const prefetched = useRef<{ page: number; gen: number; promise: Promise<ListingsResponse | null> } | null>(null);
  const restoreScroll = useRef(false);
  // The page the current search opened on: its cards keep the full
  // entrance cascade, appended pages get the short one.
  const startPage = useRef(1);
  const prependAnchor = useRef<{ page: number; top: number } | null>(null);
  const sentinelRef = useRef<HTMLDivElement | null>(null);
  // The placeholder page above the first loaded one (a refresh on ?page=N).
  const topRef = useRef<HTMLDivElement | null>(null);
  const listTopRef = useRef<HTMLDivElement | null>(null);
  const onFilterRef = useRef(onFilter);
  onFilterRef.current = onFilter;
  // Detail panel target. We keep the external_id (drives the live
  // fetch), the card's source_url (so the footer actions stay valid
  // even before / if the live fetch fails), and the card's lat/lng
  // (mini-map fallback while the live detail - which carries its own
  // coords - is in flight).
  const [active, setActive] = useState<{
    externalId: string;
    listingId: string;
    sourceUrl: string;
    lat: number | null;
    lng: number | null;
    overpricedPct: number | null;
    thumbnailUrl: string | null;
    startPhoto: string | null;
  } | null>(null);

  // Drawer opens this session; the fifth one offers a saved search.
  const [opens, setOpens] = useState(0);

  function openDetail(listing: ListingCard) {
    // external_id is the bina numeric id; fall back to nothing if a
    // legacy row lacks it (those can't be live-fetched).
    if (!listing.external_id) return;
    setOpens(countListingOpen());
    setActive({
      externalId: listing.external_id,
      listingId: listing.id,
      sourceUrl: listing.source_url,
      lat: listing.latitude,
      lng: listing.longitude,
      overpricedPct: listing.ai_overpriced_pct,
      // The image the card showed: a photo filter's match (the floor
      // plan for "plan") when it has one, and the drawer opens on it.
      thumbnailUrl: listing.match_photo ?? listing.thumbnail_url ?? null,
      startPhoto: listing.match_photo ?? null,
    });
  }

  // A link pasted into the search bar, found in the index: the drawer
  // opens on it exactly as a tap on its card would.
  useEffect(() => {
    if (openRequest) openDetail(openRequest.card);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openRequest]);

  // Deep link: /listings?open=<listing id> opens the drawer on arrival
  // (the finder's example listings land here instead of /l/<id>, which
  // only serves menzil-hosted listings). Prefer the card already on the
  // page; fall back to the detail endpoint when it sits on another page.
  const openParam = searchParams.get("open");
  const openedRef = useRef<string | null>(null);
  useEffect(() => {
    if (!openParam || openedRef.current === openParam) return;
    const hit = (infinite ? blocks.flatMap((b) => b.results) : data?.results ?? []).find((l) => l.id === openParam);
    if (hit) {
      openedRef.current = openParam;
      openDetail(hit);
      return;
    }
    if (!data) return; // wait for the first load
    let cancelled = false;
    fetchListingDetail(openParam)
      .then((d) => {
        if (cancelled) return;
        openedRef.current = openParam;
        setActive({
          externalId: d.external_id,
          listingId: openParam,
          sourceUrl: d.source_url,
          lat: d.latitude,
          lng: d.longitude,
          overpricedPct: null,
          thumbnailUrl: null,
          startPhoto: null,
        });
      })
      .catch(() => { openedRef.current = openParam; });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openParam, data, blocks]);

  // Report the seeded total to the parent on mount so pagination shows
  // up immediately when we render from SSR data (the effect below would
  // otherwise be skipped on the first pass, leaving pagination blank
  // until the first client navigation).
  useEffect(() => {
    if (canSeed && initialData) {
      const pages = Math.ceil(initialData.total / PAGE_SIZE);
      onTotalChange?.(initialData.total, pages);
    }
    // Run once on mount for the seeded case only.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    // First effect run after an SSR seed: we already have the matching
    // data, so don't refetch. Clear the flag so subsequent param/page
    // changes fetch normally.
    if (skipFirstFetch.current) {
      skipFirstFetch.current = false;
      return;
    }

    let cancelled = false;

    setLoading(true);
    setError(null);
    // NOTE: pagination/count deliberately NOT reset here - the previous
    // results stay on screen (dimmed) during the refetch, and blanking
    // the count/pagination under them made every filter change flash.

    // Build params from searchParams (shared with the map view), then
    // enforce page size + current page on top.
    const params = apiParamsFromSearch(searchParams);
    params["page_size"] = String(PAGE_SIZE);
    // Infinite: a new search starts at the URL's page (1 after a filter
    // change, the reader's page after a refresh).
    const start = infinite ? Math.max(1, pageRef.current) : page;
    params["page"] = String(start);
    if (!params["sort"]) params["sort"] = "newest";
    if (infinite) {
      gen.current += 1;
      prefetched.current = null;
      busy.current = false;
      setMore(null);
      setMoreError(false);
      restoreScroll.current = start > 1;
    }

    fetchListings(params)
      .then((res) => {
        if (!cancelled) {
          setData(res);
          if (infinite) {
            startPage.current = start;
            setBlocks([{ page: start, results: res.results }]);
          }
          const pages = Math.ceil(res.total / PAGE_SIZE);
          onTotalChange?.(res.total, pages);
        }
      })
      .catch((err: Error) => {
        if (!cancelled) {
          setError(err.message);
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, infinite ? [filterKey] : [searchParams.toString(), page]);

  // ── Infinite scroll ──
  const totalPages = data ? Math.ceil(data.total / PAGE_SIZE) : 0;
  const firstPage = blocks[0]?.page ?? 1;
  const lastPage = blocks[blocks.length - 1]?.page ?? 0;
  const hasNext = infinite && lastPage > 0 && lastPage < totalPages;

  function fetchPage(p: number): Promise<ListingsResponse | null> {
    const hit = prefetched.current;
    if (hit && hit.page === p && hit.gen === gen.current) return hit.promise;
    const up = prevPrefetched.current;
    if (up && up.page === p && up.gen === gen.current) return up.promise;
    const params = apiParamsFromSearch(searchParams);
    params["page_size"] = String(PAGE_SIZE);
    params["page"] = String(p);
    if (!params["sort"]) params["sort"] = "newest";
    return fetchListings(params).catch(() => null);
  }

  function loadPage(dir: "next" | "prev", how: "auto" | "click") {
    if (!infinite || busy.current || resetting.current || !data) return;
    const p = dir === "next" ? lastPage + 1 : firstPage - 1;
    if (p < 1 || p > totalPages) return;
    busy.current = true;
    setMore(dir);
    setMoreError(false);
    const g = gen.current;
    fetchPage(p).then((res) => {
      if (g !== gen.current) return;
      busy.current = false;
      setMore(null);
      if (!res) { setMoreError(true); return; }
      // Newest-first lists shift while the reader scrolls: a card already
      // shown never appears twice.
      setBlocks((cur) => {
        if (cur.some((b) => b.page === p)) return cur;
        const seen = new Set(cur.flatMap((b) => b.results.map((r) => r.id)));
        const block = { page: p, results: res.results.filter((r) => !seen.has(r.id)) };
        if (dir === "prev") {
          // Anchor on the page block the reader is looking at, measured
          // just before the earlier page goes in above it.
          const first = cur[0];
          const el = first && document.querySelector<HTMLElement>(`[data-listings-page="${first.page}"]`);
          if (el) {
            prependAnchor.current = { page: first.page, top: el.getBoundingClientRect().top };
            // The browser's own scroll anchoring would correct the same
            // insertion a second time.
            document.documentElement.style.overflowAnchor = "none";
          }
          return [block, ...cur];
        }
        return [...cur, block];
      });
      trackEvent("listings.page_loaded", { page: p, dir, how, loaded: blocks.length + 1 });
    });
  }

  // The next page is fetched as soon as the current one shows, so the
  // append is instant when the reader gets there.
  useEffect(() => {
    if (!hasNext) return;
    const p = lastPage + 1;
    if (prefetched.current?.page === p && prefetched.current.gen === gen.current) return;
    prefetched.current = { page: p, gen: gen.current, promise: fetchPage(p) };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasNext, lastPage, filterKey]);

  // Back to the start of the results with the filters in view. Deep
  // after a refresh (the list starts at ?page=N) that means page 1, not
  // the top of what happens to be loaded.
  // No page loads while it runs: the jump itself reads as scrolling up.
  const resetting = useRef(false);
  function backToTop() {
    trackEvent("listings.back_to_top", { page: firstPage, loaded: blocks.length });
    setShowTop(false);
    if (firstPage === 1) {
      window.scrollTo({ top: 0, behavior: "instant" as ScrollBehavior });
      return;
    }
    resetting.current = true;
    gen.current += 1;
    prefetched.current = null;
    prevPrefetched.current = null;
    busy.current = false;
    setMore(null);
    setMoreError(false);
    const g = gen.current;
    fetchPage(1).then((res) => {
      if (g !== gen.current) return;
      if (res) {
        startPage.current = 1;
        flushSync(() => setBlocks([{ page: 1, results: res.results }]));
      }
      window.scrollTo({ top: 0, behavior: "instant" as ScrollBehavior });
      requestAnimationFrame(() => { lastY.current = window.scrollY; resetting.current = false; });
    });
  }

  // Same for the page above, after a refresh deep in the list.
  const prevPrefetched = useRef<{ page: number; gen: number; promise: Promise<ListingsResponse | null> } | null>(null);
  useEffect(() => {
    if (!infinite || firstPage <= 1 || !blocks.length) return;
    const p = firstPage - 1;
    if (prevPrefetched.current?.page === p && prevPrefetched.current.gen === gen.current) return;
    prevPrefetched.current = { page: p, gen: gen.current, promise: fetchPage(p) };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [infinite, firstPage, blocks.length, filterKey]);

  // One passive scroll listener, read once per frame, does two jobs:
  // attach the next page while the end is within AHEAD_PX (only once the
  // reader has scrolled, or when the screen is not full, so a visit that
  // never scrolls costs the single prefetch), and keep the URL's ?page=
  // on the page under a line 40% down the screen, written in place (no
  // navigation, no server round trip) so a refresh or a shared link
  // lands on it.
  const loadRef = useRef(loadPage);
  loadRef.current = loadPage;
  // Pages keep attaching until the results end (owner, 2026-10-13: a
  // button every 6 pages was a wall); the button only stands in after a
  // failed load.
  const canAuto = infinite && hasNext && !moreError;
  const canAutoPrev = infinite && firstPage > 1 && !moreError;
  const lastY = useRef(0);
  const downFrom = useRef<number | null>(null);
  useEffect(() => {
    if (!infinite || !blocks.length) return;
    let frame = 0;
    const check = () => {
      frame = 0;
      const y = window.scrollY;
      const up = y < lastY.current;
      const vh = window.innerHeight;
      // Back-to-top: shown once the reader is two screens down and turns
      // back up; hidden on the way down and near the top.
      // Hidden only after ~160 px of steady downward scroll, so a small
      // wobble never blinks it (owner: "smoothen the disappearance").
      if (y < vh * 2) setShowTop(false);
      else if (y < lastY.current - 4) { setShowTop(true); downFrom.current = null; }
      else if (y > lastY.current + 4) {
        downFrom.current ??= lastY.current;
        if (y - downFrom.current > 160) setShowTop(false);
      }
      lastY.current = y;
      const el = sentinelRef.current;
      if (canAuto && el) {
        const top = el.getBoundingClientRect().top;
        if (top < vh || (y > 0 && top < vh + AHEAD_PX)) loadRef.current("next", "auto");
      }
      // Scrolling up toward the placeholder page attaches the real one.
      const topEl = topRef.current;
      if (canAutoPrev && up && topEl && topEl.getBoundingClientRect().bottom > -AHEAD_PX) loadRef.current("prev", "auto");
      const line = window.innerHeight * 0.4;
      let current: number | null = null;
      for (const b of document.querySelectorAll<HTMLElement>("[data-listings-page]")) {
        if (b.getBoundingClientRect().top <= line) current = Number(b.dataset.listingsPage);
        else break;
      }
      if (current == null) current = Number(document.querySelector<HTMLElement>("[data-listings-page]")?.dataset.listingsPage || 1);
      const u = new URL(window.location.href);
      if (current > 1) u.searchParams.set("page", String(current));
      else u.searchParams.delete("page");
      // The entry's own state stays: Next's router tree and an open
      // panel's Back-to-close marker live there (null wiped both).
      if (u.href !== window.location.href) window.history.replaceState(window.history.state, "", `${u.pathname}${u.search}${u.hash}`);
    };
    const onScroll = () => { if (!frame) frame = requestAnimationFrame(check); };
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll, { passive: true });
    // Once after each change: a short page may already need the next one.
    // No URL write before the reader scrolls (the server page is page 1).
    const first = requestAnimationFrame(() => { if (window.scrollY > 0 || canAuto) check(); });
    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
      cancelAnimationFrame(first);
      if (frame) cancelAnimationFrame(frame);
    };
  }, [infinite, blocks, canAuto, canAutoPrev]);

  // Prepending earlier pages keeps the reader's card where it was.
  useLayoutEffect(() => {
    const a = prependAnchor.current;
    if (!a) return;
    prependAnchor.current = null;
    const el = document.querySelector<HTMLElement>(`[data-listings-page="${a.page}"]`);
    if (el) window.scrollBy(0, el.getBoundingClientRect().top - a.top);
    const id = requestAnimationFrame(() => { document.documentElement.style.overflowAnchor = ""; });
    return () => { cancelAnimationFrame(id); document.documentElement.style.overflowAnchor = ""; };
  }, [blocks]);

  // After a refresh on ?page=N the list starts at page N: bring its top
  // into view instead of the browser's restored offset.
  useEffect(() => {
    const firstBlock = document.querySelector<HTMLElement>("[data-listings-page]");
    if (!restoreScroll.current || !blocks.length || !firstBlock) return;
    restoreScroll.current = false;
    const top = firstBlock.getBoundingClientRect().top + window.scrollY - 96;
    window.scrollTo({ top: Math.max(0, top) });
  }, [blocks]);


  // Reaching the end, and the button taking over, are tracked once per
  // search.
  const endSeen = useRef<string | null>(null);
  useEffect(() => {
    if (!infinite || !data || hasNext || !blocks.length || endSeen.current === filterKey) return;
    if (totalPages <= 1) return;
    endSeen.current = filterKey;
    trackEvent("listings.end_reached", { pages: blocks.length, total: data.total });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [infinite, hasNext, blocks.length, filterKey]);

  function pageHref(p: number): string {
    const q = new URLSearchParams(searchParams.toString());
    q.delete("open");
    if (p > 1) q.set("page", String(p));
    else q.delete("page");
    return `?${q.toString()}`;
  }

  // The detail panel is rendered alongside every grid state (via a
  // portal) so it can stay open / animate closed even if a background
  // refetch flips the grid back into a loading/empty branch.
  const panel = (
    <ListingDetailPanel
      externalId={active?.externalId ?? null}
      listingId={active?.listingId ?? null}
      sourceUrl={active?.sourceUrl ?? null}
      lat={active?.lat ?? null}
      lng={active?.lng ?? null}
      overpricedPct={active?.overpricedPct ?? null}
      thumbnailUrl={active?.thumbnailUrl ?? null}
      startPhoto={active?.startPhoto ?? null}
      onClose={() => setActive(null)}
      variant="split"
      // Commercial premises and garages only list under their own
      // category: the drawer drops the analyze action from the start.
      category={searchParams.get("category")}
    />
  );

  // Distinct per RESULT SET - forces a clean grid remount when new data
  // lands so recycled <Image> nodes never strand their lazy-load on page
  // change. Derived from the data (not the URL): the previous set stays
  // mounted - dimmed - while its replacement is in flight, and remounts
  // exactly once when the new rows arrive (which also replays the card
  // entrance cascade).
  // Includes the view so switching grid <-> rows replays the entrance
  // cascade (the switch itself is the micro-animation the owner asked for).
  const gridKey = data ? `${view}|p${data.page}|${data.results.map((r) => r.id).join(",")}` : "empty";

  // Column-count state for the docked split pane. Docks immediately on
  // open, but UN-docks only after the panel's 200ms exit animation has
  // freed its 520px - flipping the classes at close time made every
  // card shrink for a beat (more columns inside a still-narrow row)
  // and then grow once the panel unmounted.
  const [docked, setDocked] = useState(false);
  const lastOpened = useRef<string | null>(null);
  useEffect(() => {
    if (active) lastOpened.current = active.listingId;
    // The 4->3 column reflow repositions every card in one hard jump.
    // Where the View Transitions API exists, wrap the flip so the
    // browser crossfades old layout -> new layout instead (fallback:
    // the plain flip). Reduced-motion users skip it via the
    // ::view-transition override in globals.css.
    // The column change re-flows every page above the reader, which
    // after a few infinite pages moved the view by thousands of pixels
    // (owner, 2026-10-13). The card being read (the opened one when it
    // is on screen, else the one at 40% height) is put back exactly
    // where it was, in the same frame as the flip.
    // The anchor is taken when the panel starts to close: by the time
    // the columns flip, the panel's exit has already widened the row
    // and the browser has moved the page.
    const apply = (next: boolean, anchor = readingCard(lastOpened.current)) => {
      const correct = () => {
        if (!anchor) return;
        const el = document.querySelector<HTMLElement>(`[data-listing-card="${CSS.escape(anchor.id)}"]`);
        if (!el) return;
        const d = el.getBoundingClientRect().top - anchor.top;
        if (Math.abs(d) < 1) return;
        document.documentElement.style.overflowAnchor = "none";
        window.scrollBy(0, d);
        requestAnimationFrame(() => { document.documentElement.style.overflowAnchor = ""; });
      };
      const flip = () => {
        flushSync(() => setDocked(next));
        correct();
      };
      const doc = document as Document & {
        startViewTransition?: (cb: () => void) => { finished: Promise<void> };
      };
      if (doc.startViewTransition) {
        // Cards above re-measure while the crossfade runs (content-
        // visibility, the dense card variant): correct once more after.
        doc.startViewTransition(flip).finished.then(correct, correct);
      } else {
        flip();
        requestAnimationFrame(correct);
      }
    };
    if (active) {
      apply(true);
      return;
    }
    const anchor = readingCard(lastOpened.current);
    const id = setTimeout(() => apply(false, anchor), 240);
    return () => clearTimeout(id);
  }, [active]);

  const gridCols = view === "list"
    ? "grid-cols-1"
    : docked
    ? "grid-cols-2 sm:grid-cols-3 lg:grid-cols-2 xl:grid-cols-3"
    : "grid-cols-2 sm:grid-cols-3 lg:grid-cols-4";

  // Under a search for one residence, after its last page (or its empty
  // state): the deals of the residences around it, in a section of their
  // own (feedback ticket 65). Any other search renders nothing and asks
  // nothing.
  const nearby = nearbyQueryFromSearch(searchParams);
  const nearbyBlock = nearby ? (
    <NearbyDeals
      query={nearby}
      onOpen={openDetail}
      activeId={active?.listingId ?? null}
      gridClassName={gridCols}
      dense={docked}
      surface="listings"
      className="mt-10"
    />
  ) : null;

  // The facts the search asks for, the merged questions' sides included
  // (has_repair=yes asks for "ready"): a card does not repeat them.
  const activeFactsKey = askedFacts(searchParams).join(",");
  const activeFacts = useMemo(() => (activeFactsKey ? activeFactsKey.split(",") : []), [activeFactsKey]);
  function renderCard(listing: ListingCard, i: number, quick: boolean, above = false) {
    // Hidden from the three-dot menu in this tab: gone at once (the
    // server leaves it out of the next page and the map).
    if (hiddenIds.has(listing.id)) return null;
    return (
      // Entrance cascade: each card rises in with a small per-index
      // delay (capped so deep grids don't wait), replayed whenever the
      // result set changes. Appended pages use the short variant: the
      // reader is scrolling and must not wait for cards to settle.
      <div
        key={listing.id}
        data-listing-card={listing.id}
        // Pages inserted above the reader skip content-visibility: its
        // 20rem placeholder shrank to the real card height after the
        // scroll was corrected, and the view jumped (phones, 2026-10-13).
        className={`${quick ? "card-enter-quick" : i < 4 ? "card-rise" : "card-enter"} h-full ${above ? "" : view === "list" ? "cv-auto-sm" : "cv-auto"}`}
        style={{ animationDelay: quick ? `${Math.min(i, 7) * 20}ms` : `${Math.min(i, 11) * 35}ms` }}
      >
        <ListingBrowseCard
          listing={listing}
          onOpen={openDetail}
          hidePlace={hidePlace}
          href={(() => { const lp = listingPath(listing.id); return lp ? `/${locale}${lp}` : undefined; })()}
          variant={view === "list" ? "row" : "grid"}
          dense={docked}
          active={active?.listingId === listing.id}
          priority={!quick && i < 4}
          onFilter={(params) => onFilterRef.current?.(params)}
          // Transaction badge only when the grid MIXES modes
          // (leased=all). Absent param = sale-only default and
          // leased=true/false pin one mode - in all three cases
          // every card saying "Satış"/"Kirayə" was pure noise.
          showTransaction={searchParams.get("leased") === "all"}
          activeFacts={activeFacts}
        />
      </div>
    );
  }

  const skeletonRows = (n: number) => (
    <div className={`grid gap-3 ${gridCols}`} style={{ overflowAnchor: "none" }} aria-hidden>
      {Array.from({ length: n }).map((_, i) => <SkeletonCard key={i} />)}
    </div>
  );
  const moreBtn = "inline-flex h-10 items-center gap-2 rounded-lg border border-gray-200 bg-white px-4 text-sm font-semibold text-gray-700 transition-colors hover:border-gray-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500";

  // One grid per page (24 fills 2, 3 and 4 columns evenly, so the pages
  // read as one grid). Each page keeps its own subtree: appending never
  // remounts the cards above, so their images and scroll stay put.
  const infiniteList = (
    <div
      ref={listTopRef}
      // globals.css: while pages remain, the footer is never the scroll
      // anchor (a fling that outran loading showed it, and anchoring on
      // it threw the reader down when the page attached).
      data-infinite-more={hasNext ? "" : undefined}
      className={`space-y-3 transition-opacity duration-300 ${loading ? "opacity-40 pointer-events-none" : ""}`}
    >
      {/* After a refresh on ?page=N the earlier pages attach as the
          reader scrolls up (owner, 2026-10-13: no "earlier results"
          button). A placeholder page holds their place; the real page
          swaps in with the reader's card kept where it was. Crawlers
          follow the hidden link; the button only returns after a failed
          load. */}
      {firstPage > 1 && (
        moreError ? (
          <div className="flex justify-center pb-1">
            <a
              href={pageHref(firstPage - 1)}
              rel="prev"
              onClick={(e) => { e.preventDefault(); loadPage("prev", "click"); }}
              className={moreBtn}
            >
              {t("listings.prev")}
            </a>
          </div>
        ) : (
          <div ref={topRef}>
            <a href={pageHref(firstPage - 1)} rel="prev" className="sr-only">{t("listings.prev")}</a>
            {skeletonRows(PAGE_SIZE)}
          </div>
        )
      )}
      {blocks.map((b) => (
        <div
          key={`${filterKey}|${view}|p${b.page}`}
          data-listings-page={b.page}
          // Each row as tall as its own tallest card (not auto-rows-fr,
          // which gave every row of a page the tallest card's height:
          // with the side panel open, one long title stretched the whole
          // page, owner 2026-10-13).
          className={`grid gap-3 ${gridCols}`}
        >
          {b.results.map((listing, i) => renderCard(listing, i, b.page !== startPage.current, b.page < startPage.current))}
        </div>
      ))}
      {/* The next pages' place is held by two placeholder pages, so a hard
          fling lands on placeholders, never on the footer (reaching the
          footer first let the browser pin it and throw the reader down
          when the page attached). */}
      <div ref={sentinelRef} />
      {hasNext && !moreError && skeletonRows(Math.max(1, Math.min(PAGE_SIZE * 2, (data?.total ?? 0) - lastPage * PAGE_SIZE)))}
      {/* Back to top: pinned to the bottom of the list column (clear of
          the side panel and the phone nav), taking no space. */}
      <div className="pointer-events-none sticky bottom-24 z-20 h-0 sm:bottom-6">
        <div
          className={`absolute bottom-0 left-1/2 transition-[opacity,translate,scale] ease-out ${
            showTop ? "-translate-x-1/2 translate-y-0 scale-100 opacity-100 duration-200" : "-translate-x-1/2 translate-y-3 scale-95 opacity-0 duration-300"
          }`}
        >
          <button
            type="button"
            onClick={backToTop}
            tabIndex={showTop ? 0 : -1}
            aria-hidden={!showTop}
            className={`${showTop ? "pointer-events-auto" : ""} inline-flex h-10 items-center gap-1.5 rounded-full border border-gray-200 bg-white px-4 text-sm font-semibold text-gray-900 shadow-[0_12px_32px_-16px_rgba(15,23,42,0.45)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500`}
          >
            <ArrowUp className="h-4 w-4" aria-hidden />
            {t("listings.top")}
          </button>
        </div>
      </div>
      <div className="flex flex-col items-center gap-2 pt-3">
        {hasNext && more !== "next" && moreError ? (
          <>
            {moreError && <p className="text-sm text-gray-500">{t("listings.more.error")}</p>}
            <a
              href={pageHref(lastPage + 1)}
              rel="next"
              onClick={(e) => { e.preventDefault(); loadPage("next", "click"); }}
              className={moreBtn}
            >
              {t("listings.more")}
            </a>
          </>
        ) : hasNext ? (
          // Crawlers follow this; readers see the placeholder page.
          <a href={pageHref(lastPage + 1)} rel="next" className="sr-only">{t("listings.more")}</a>
        ) : blocks.length > 0 && totalPages > 1 ? (
          <p className="py-6 text-sm text-gray-500">{t("listings.end")}</p>
        ) : null}
      </div>
    </div>
  );

  let body: React.ReactNode;
  if (loading && !data) {
    // Cold load only (no previous results to keep on screen): skeleton
    // grid instead of a centred spinner - card-shaped placeholders keep
    // the layout stable while the first page resolves. Filter / page /
    // sort changes never come through here any more: the previous set
    // stays mounted (dimmed) below and swaps in place.
    body = (
      <div>
        <div className="h-4 w-40 rounded bg-gray-200 animate-pulse mb-4" />
        {/* Equal rows from sm up; on phones every row took the tallest card's
            height across the whole grid and left a hole under the facts
            (owner, 2026-10-09). */}
        <div className="grid grid-cols-2 gap-3 sm:auto-rows-fr sm:grid-cols-3 lg:grid-cols-4">
          {Array.from({ length: PAGE_SIZE }).map((_, i) => (
            <SkeletonCard key={i} />
          ))}
        </div>
      </div>
    );
  } else if (error) {
    body = (
      <div className="flex flex-col items-center justify-center py-20 gap-2 text-red-500 dark:text-red-400">
        <SearchX className="w-8 h-8" />
        <p className="text-sm">{t("listings.results.error")}</p>
        <p className="text-xs text-gray-600">{error}</p>
      </div>
    );
  } else if (!data || data.results.length === 0) {
    body = (
      <div className="flex flex-col items-center justify-center py-20 gap-2">
        <SearchX className="w-10 h-10 text-gray-300" />
        <p className="text-base font-semibold text-gray-700">
          {t("listings.results.empty.title")}
        </p>
        <p className="text-sm text-gray-500">
          {t("listings.results.empty.desc")}
        </p>
        {/* The filter to drop, with what it brings back (counted only
            when the page is empty). */}
        <ZeroResultsHint searchParams={searchParams} onRemove={(updates) => onFilterRef.current?.(updates)} />
      </div>
    );
    if (nearbyBlock) body = <div>{body}{nearbyBlock}</div>;
  } else {
    // A refetch is in flight (filter / page / sort change): keep the
    // current cards rendered, dim them, float an "updating" pill on
    // top - the layout never collapses to skeletons mid-browse.
    const stale = loading;
    body = (
      <div className="relative">
        {/* Result count is rendered by the page (next to sort + map), not
            here, so it shares a row with those controls. */}
        {stale && (
          <div className="pointer-events-none absolute inset-x-0 top-4 z-10 flex justify-center">
            <span
              style={{ transformOrigin: "center top" }}
              className="dropdown-enter flex items-center gap-2.5 rounded-full border border-gray-200 bg-white px-4 py-2 text-[13px] font-semibold text-gray-800 shadow-[0_12px_32px_-12px_rgba(21,23,31,0.3)]"
            >
              <Loader2 className="h-4 w-4 animate-spin text-gray-400" />
              {t("listings.results.updating")}
            </span>
          </div>
        )}

        {/* Fifth drawer of the session: offer the search as an alert. */}
        <DealAlertNudge searchParams={searchParams} opens={opens} />

        {/* Grid.
            The container key is tied to the RESULT SET (see gridKey) so
            React unmounts the previous page's cards and mounts a fresh
            subtree when new rows land. Without this, React recycles the
            existing <Image> DOM nodes by position and only swaps their
            `src` - the native lazy-loader, having already deferred the
            below-the-fold images on the prior page, never re-fires for
            the new srcs, so page 2+ rendered with broken / blank
            thumbnails. A fresh subtree restarts each image's lazy
            lifecycle cleanly - and replays the entrance cascade. */}
        {infinite ? infiniteList : (
        <div
          key={gridKey}
          // When the detail pane is docked (lg+ inline split), it eats
          // ~540px on the right, so the grid must drop to fewer columns
          // or the cards get squashed. Below lg the pane is an overlay
          // (no docking), so the column counts stay full there.
          // Equal rows from sm up only: on phones every row took the tallest
          // card's height across the whole grid (owner, 2026-10-09).
          className={`grid gap-3 transition-opacity duration-300 sm:auto-rows-fr ${
            stale ? "opacity-40 pointer-events-none" : ""
          } ${gridCols}`}
        >
          {data!.results.map((listing: ListingCard, i: number) => renderCard(listing, i, false))}
        </div>
        )}
        {/* Once the last page is in: the pages above never push it down. */}
        {(!infinite || !hasNext) && nearbyBlock}
      </div>
    );
  }

  // Split layout: the grid shifts left while the detail pane occupies
  // the right side as part of the content (desktop); on mobile the
  // panel renders the overlay bottom sheet instead, so the flex
  // wrapper simply collapses to the body alone.
  return (
    <div className="flex items-start">
      <div className="flex-1 min-w-0">{body}</div>
      {panel}
    </div>
  );
}

/* ── Skeleton card ──────────────────────────────────────────────────
 * Shape-matches ListingBrowseCard: a 4:3 image block on top, then the
 * price row, facts row, and location row as shimmer lines. Same border
 * + rounding as the real card so the grid doesn't jump on hand-off. */

/* ── Page shell ──────────────────────────────────────────────────────
 * The PUBLIC, list-only browse experience (no Mapbox, so it stays light
 * + SEO-friendly). The map view moved to the gated /map page as a
 * "Listings" layer; the "View on map" link below jumps there carrying
 * the current filters.
 *
 * This is the interactive client tree the server `page.tsx` renders. It
 * owns the URL (filters / sort / pagination) via useSearchParams +
 * useRouter, and forwards the server-fetched `initialData` straight down
 * to the grid so the first paint / crawl contains real cards. Lives in
 * this module (rather than its own file) so the grid + its shell are one
 * client unit the server component can import without a circular import. */

/** Landing mode: a /menziller page renders this same shell with its
 *  filters pre-applied and its own SEO content around the grid. */
export interface ListingsLanding {
  /** The landing page's filters as /listings params. */
  baseQuery: string;
  /** Replaces the default page title block. */
  header: ReactNode;
  /** Rendered under the grid and pagination. */
  footer: ReactNode;
  /** The page's place name (cards stop repeating it). */
  placeName?: string | null;
}

function ListingsShell({
  urlParams,
  initialData,
  landing,
  seedKey,
  intro,
}: {
  /** The URL's query: useSearchParams on /listings, read after
   *  hydration on a landing page (see LandingShell). */
  urlParams: ReadonlyURLSearchParams;
  initialData?: ListingsResponse | null;
  landing?: ListingsLanding;
  seedKey?: string;
  intro?: ReactNode;
}) {
  const t = useT();
  const { locale } = useLocale();
  // Admin kill switch for the AI bar (the parser endpoint refuses too).
  const magicSearchOn = useFeatureFlag("magic_search");
  const router = useRouter();
  // The effective filter state: on /listings the URL; on a landing page
  // its own filters, with anything in the URL on top.
  const baseQuery = landing?.baseQuery;
  const searchParams = useMemo(() => {
    if (!baseQuery) return urlParams;
    const merged = new URLSearchParams(baseQuery);
    for (const k of new Set(Array.from(urlParams.keys()))) {
      merged.delete(k);
      for (const v of urlParams.getAll(k)) merged.append(k, v);
    }
    return merged as unknown as ReadonlyURLSearchParams;
  }, [baseQuery, urlParams]);
  // Every filter, sort or page change: in place on /listings; from a
  // landing page it moves to /listings with the whole state, since the
  // landing URL stands for exactly one search.
  function go(qs: string) {
    router.push(landing ? `/${locale}/listings?${qs}` : `?${qs}`);
  }
  // Total result count + pages - surfaced by ListingsGrid after its fetch
  // (or from the SSR seed) completes, then passed down to Pagination so
  // we avoid a second API call. Seed it from initialData when present so
  // the SSR HTML can include pagination too (and the count line isn't
  // blank for crawlers).
  const [paginationInfo, setPaginationInfo] = useState<{ total: number; pages: number } | null>(
    initialData
      ? { total: initialData.total, pages: Math.ceil(initialData.total / PAGE_SIZE) }
      : null,
  );
  // Advanced-search modal (opened from the filter bar's "Filtrlər").
  const [advancedOpen, setAdvancedOpen] = useState(false);
  // A listing link pasted into the search bar and found in the index
  // opens in the grid's drawer (ListingsGrid openRequest).
  const [openRequest, setOpenRequest] = useState<{ card: ListingCard; seq: number } | null>(null);
  const openFromSearch = useCallback((card: ListingCard) => {
    setOpenRequest((cur) => ({ card, seq: (cur?.seq ?? 0) + 1 }));
  }, []);

  // A bare /listings opens on the profile's own search (owner, 2026-10-05:
  // "if I'm buying, I have sale, flat, ... already applied"): the goal's
  // leased/category/rooms/places go into the URL once. No chip says where
  // they came from (owner, 2026-10-30: "remove it completely"); Reset
  // clears them and holds for the session.
  const { user } = useAuth();
  const personal = usePersonal();
  const profileParams = personal.data?.intent?.profile?.params ?? null;
  useEffect(() => {
    if (landing || !user || !profileParams) return;
    if (Array.from(urlParams.keys()).length > 0) return;
    try { if (sessionStorage.getItem("menzil.listings.profile_off") === "1") return; } catch { /* storage off */ }
    const next = new URLSearchParams();
    for (const [k, v] of Object.entries(profileParams)) {
      if (k === "sort" || k === "deal_tier" || v == null) continue;
      for (const x of ([] as string[]).concat(v)) next.append(k, x);
    }
    if (!next.toString()) return;
    next.set("from", "profile");
    router.replace(`?${next.toString()}`);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [landing, user, profileParams, urlParams]);

  // One card layout: the grid/list switch was removed (owner,
  // 2026-10-01) after 3 people outside the team tried the list in a week.
  const view = "grid" as const;

  // Sort + pagination commit to the URL immediately (outside the draft
  // filter / Search-button flow).
  function setParam(key: string, value: string | null) {
    const next = new URLSearchParams(searchParams.toString());
    if (value === null || value === "") {
      next.delete(key);
    } else {
      next.set(key, value);
    }
    if (key !== "page") next.delete("page");
    go(next.toString());
    // Page flips return to the top INSTANTLY - a long smooth scroll over
    // the whole page reads as showy, and the dimmed old set + updating
    // pill already carry the continuity.
    if (key === "page") window.scrollTo({ top: 0 });
  }

  // Commit the whole draft filter set from the advanced modal at once.
  // `sort` is preserved (it lives outside the panel); page resets to 1.
  function commitFilters(params: Record<string, string | string[]>) {
    // Analytics: which filters the advanced modal applied.
    captureEvent("listings_filters_applied", { surface: "list", filters: Object.keys(params) });
    const next = new URLSearchParams();
    const currentSort = searchParams.get("sort");
    if (currentSort) next.set("sort", currentSort);
    // The panel has no control for `category` (a landing page pins
    // apartments), so it would otherwise drop it and let houses in.
    const currentCategory = searchParams.get("category");
    if (currentCategory) next.set("category", currentCategory);
    for (const [k, v] of Object.entries(params)) {
      if (Array.isArray(v)) {
        v.forEach((item) => { if (item) next.append(k, item); });
      } else if (v) {
        next.set(k, v);
      }
    }
    go(next.toString());
  }

  function resetAll() {
    // Keep sort, drop everything else. Reset means empty: an empty URL
    // would otherwise be re-seeded with the profile's criteria below, so
    // Reset read as "back to my selection" (owner, 2026-10-07).
    try { sessionStorage.setItem("menzil.listings.profile_off", "1"); } catch { /* storage off */ }
    const next = new URLSearchParams();
    const currentSort = searchParams.get("sort");
    if (currentSort) next.set("sort", currentSort);
    go(next.toString());
    // The search bar keeps the typed (not yet applied) text in its own
    // state, which the URL push cannot reach: tell it to clear too, so
    // the red Reset empties the input as well as the chips.
    window.dispatchEvent(new CustomEvent("menzil:filters-reset"));
  }

  // Instant single-/multi-param commit for the quick filter bar (hybrid:
  // bar chips apply immediately, the advanced modal batches via
  // commitFilters). Merges the given keys into the current URL, preserving
  // everything else; resets to page 1. A null / "" / [] value clears the key.
  function mergeParams(raw: Record<string, string | string[] | null>) {
    const updates = placeReplacesRadius(raw, searchParams as unknown as URLSearchParams);
    haptic(6); // quick-bar filter commits get a tactile tick on touch
    const next = new URLSearchParams(searchParams.toString());
    for (const [k, v] of Object.entries(updates)) {
      next.delete(k);
      if (Array.isArray(v)) {
        v.forEach((item) => { if (item) next.append(k, item); });
      } else if (v) {
        next.set(k, v);
      }
      // Analytics: which quick filters get used (sets only, not clears).
      if (v !== null && v !== "" && !(Array.isArray(v) && v.length === 0)) {
        captureEvent("listings_filter_used", { filter: k, surface: "list" });
      }
    }
    next.delete("page");
    // First-party lead-gen row: the full filter state AFTER this commit
    // (not just the changed key) so a session's search intent can be
    // reconstructed and aggregated per area/complex for B2B handover.
    // Sort/page changes are navigation, not intent - skipped.
    const changedKeys = Object.keys(updates).filter((k) => k !== "page" && k !== "sort");
    if (changedKeys.length) {
      trackEvent("listings.filters", {
        set: changedKeys.join(","),
        q: next.toString().slice(0, 220),
        // What this change did, compact (lib/listing-signals filterDiff):
        // a narrowing search reads as preference (recommender plan 2.2).
        diff: filterDiff(searchParams as unknown as URLSearchParams, next) || undefined,
      });
    }
    go(next.toString());
  }

  const sort = searchParams.get("sort") ?? "newest";
  const page = parseInt(searchParams.get("page") ?? "1", 10) || 1;

  // Active-filter count for the bar's "Filtrlər" badge and the reset
  // button: the one count every surface uses (lib/active-filter-count).
  const activeCount = countActiveFilters(searchParams);
  // A landing page's own filters are the page (its H1 says them): its
  // "Filtrlər" badge counts only what the visitor's URL adds on top.
  const landingOwnCount = useMemo(() => (baseQuery ? countActiveFilters(new URLSearchParams(baseQuery)) : 0), [baseQuery]);
  const landingExtra = Math.max(0, activeCount - landingOwnCount);
  const sellerParam = searchParams.get("seller");

  // "View on map" → the gated /map listings layer, carrying the current
  // filters (same param names round-trip). Drop list-only pagination.
  const mapQs = new URLSearchParams(searchParams.toString());
  mapQs.delete("page");
  mapQs.set("layer", "listings");
  const mapHref = `/map?${mapQs.toString()}`;

  return (
    <div className="min-h-screen bg-gray-50">
      <SharedHeader />

      <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8 py-6">
        {/* Page header - sits on the page background (no white strip + no
            border) so it doesn't read as a continuation of the dark
            SharedHeader above it. */}
        {landing ? landing.header : intro ? intro : (
        <header className="mb-6 flex items-start justify-between gap-3">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
              {t("listings.title")}
            </h1>
            <p className="mt-1 text-sm text-gray-500">
              {t("listings.subtitle")}
            </p>
          </div>
          {/* First-party listing entry point: free, moderated before
              publish. The form itself handles the signed-out case.
              Icon-only below sm: the long locale labels (ru "Новое
              объявление") shoved the h1 into a 3-line wrap and pushed
              the button off-viewport on narrow phones. */}
          {FIRST_PARTY_LISTINGS && (
            <Link
              href="/profile/listings/new"
              aria-label={t("mylistings.place_cta")}
              className="shrink-0 inline-flex items-center gap-1.5 rounded-lg bg-indigo-600 p-2.5 sm:px-3.5 sm:py-2 text-sm font-medium text-white hover:bg-indigo-700 transition-colors"
            >
              <Plus className="w-4 h-4" />
              <span className="hidden sm:inline">{t("mylistings.place_cta")}</span>
            </Link>
          )}
        </header>
        )}

        {landing ? (
          /* An area landing page: one row under its H1 (owner, 2026-10-31:
             the top was "overcrowded" and did not always fit). The page's
             own filters ARE the page and its H1 says them, so no search
             bar (1.4% of landing sessions opened it), no chips of them and
             no reset (it left the page for an empty /listings). Phones get
             the one "Filtrlər" button (DESIGN.md: phone filters behind one
             pill), desktop the price and rooms pills beside it, the two
             filters landing visitors set most. Right: the bell, the map
             (its one entry point now: the still map in the header drew 9
             taps in 24 days, docs/research/area-landings-2026-11-01.md)
             and the sort. */
          <div className="mb-4 flex items-center justify-between gap-2">
            <FiltersButton onClick={() => setAdvancedOpen(true)} activeCount={landingExtra} className="sm:hidden" />
            <div className="hidden min-w-0 sm:block">
              <ListingsFilterBar
                compact
                searchParams={searchParams}
                onMerge={mergeParams}
                onOpenAdvanced={() => setAdvancedOpen(true)}
                activeCount={landingExtra}
              />
            </div>
            <div className="flex shrink-0 items-center gap-2">
              <CreateAlertButton searchParams={searchParams} iconOnly />
              <Link
                href={mapHref}
                aria-label={t("listings.viewonmap")}
                className="inline-flex items-center gap-1.5 rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm font-medium text-gray-700 hover:border-gray-300 transition-colors"
              >
                <MapIcon className="w-4 h-4" />
                <span className="hidden lg:inline">{t("listings.viewonmap")}</span>
              </Link>
              <ListingsSortPicker value={sort} onChange={(v) => setParam("sort", v)} noValuation={isSeparate(searchParams.get("category"))} withLook={!!searchParams.get("photo")} />
            </div>
          </div>
        ) : (
        <>
        {/* Search: free text -> the same filter chips below. With the
            admin flag off the bar stays as plain keyword search (the
            parser endpoint refuses too); Enter then searches the
            description text via ?q=. */}
        {/* Phones (owner, 2026-10-29): one row, the search field and one
            "Filtrlər" button with the count of what is set (Redfin's
            pattern); the pill bar stays for desktop. The applied filters
            show as their own chip row below, not inside the field. */}
        <div className="mb-3 flex items-start gap-2">
        <div className="min-w-0 flex-1">
        <MagicSearchBar
          chipsOutside
          aiEnabled={magicSearchOn}
          onOpenListing={openFromSearch}
          onClearAll={resetAll}
          onMergeOne={(params) => {
            // Entity picks accumulate into the existing filter state.
            const merged: Record<string, string | string[] | null> = {};
            for (const [k, v] of Object.entries(params)) {
              if (Array.isArray(v)) {
                const prev = searchParams.getAll(k);
                merged[k] = [...prev.filter((x) => !v.includes(x)), ...v];
              } else {
                merged[k] = v;
              }
            }
            mergeParams(merged);
          }}
          onReplaceOne={(params) => {
            // Chip removal from the search panel: exact set/delete,
            // no accumulate semantics.
            mergeParams(params);
          }}
          onApplyAll={(params) => {
            const next = new URLSearchParams();
            const sort = searchParams.get("sort");
            // "Best deal" and "yield" order by a valuation shops and
            // garages do not have.
            const unvalued = isSeparate(typeof params.category === "string" ? params.category : null);
            if (sort && !(unvalued && (sort === "best_deal" || sort === "yield"))) next.set("sort", sort);
            for (const [k, v] of Object.entries(params)) {
              if (Array.isArray(v)) v.forEach((item) => next.append(k, item));
              else next.set(k, v);
            }
            captureEvent("listings_magic_search", {
              filters: Object.keys(params),
            });
            go(next.toString());
          }}
        />
        </div>
        <FiltersButton onClick={() => setAdvancedOpen(true)} activeCount={activeCount} className="h-10 sm:hidden" />
        </div>

        {/* Filter chips bar (full width), desktop only: phones reach every
            filter through the one "Filtrlər" button above. */}
        <div className="mb-3 hidden sm:block">
          <ListingsFilterBar
            searchParams={searchParams}
            onMerge={mergeParams}
            onOpenAdvanced={() => setAdvancedOpen(true)}
            activeCount={activeCount}
          />
        </div>
        </>
        )}

        {/* Publisher-grouping context. `?seller=` is URL-only (reached
            from the listing drawer / analysis result, never from a
            filter control) so without this banner a seller-scoped page
            would look like an unexplained subset. The seller stays
            unnamed and unnumbered: the key lives in the URL only. */}
        {sellerParam && (
          <div className="mb-3 flex items-center justify-between gap-3 rounded-xl border border-gray-200 bg-white px-3 py-2">
            <span className="flex items-center gap-2 text-sm text-gray-800 min-w-0 truncate">
              <UserRound className="w-4 h-4 shrink-0 text-gray-400" />
              {t("listings.seller.banner")}
            </span>
            <button
              type="button"
              onClick={() => setParam("seller", null)}
              className="shrink-0 inline-flex items-center gap-1 text-xs font-semibold text-gray-700 hover:text-gray-900 hover:underline underline-offset-2"
            >
              <X className="w-3.5 h-3.5" />
              {t("listings.seller.banner.clear")}
            </button>
          </div>
        )}

        {/* What is set, one removable chip each (sideways on phones). A
            landing page's pills and badge show what the visitor added. */}
        {!landing && <AppliedFilterChips searchParams={searchParams} onRemove={mergeParams} className="mb-3" />}

        {(searchParams.get("category") === "land" || searchParams.getAll("listing_kind").includes("land")) && <StateAuctionsNote />}

        {/* Result count (left) + sort & "View on map" (right), one row. A
            landing page has its count under its H1 and its tools above. */}
        {!landing && (
        <div className="mb-4 flex items-center justify-between gap-3">
          <p className="text-sm text-gray-500 min-w-0 truncate tabular-nums">
            {paginationInfo && (() => {
              const n = String(paginationInfo.total).replace(/\B(?=(\d{3})+(?!\d))/g, " ");
              return (
                <>
                  <span className="sm:hidden">{t("listings.results.count.short").replace("{n}", n)}</span>
                  <span className="hidden sm:inline">{t("listings.results.count").replace("{n}", n)}</span>
                </>
              );
            })()}
          </p>
          <div className="flex items-center gap-2 shrink-0">
            {/* Clear all active filters (keeps sort). Hidden when nothing
                is set, so it never reads as a dead control. */}
            {activeCount > 0 && (
              <button
                type="button"
                onClick={resetAll}
                // A phone shows the icon alone: the name stays.
                aria-label={t("listings.filter.reset")}
                className="inline-flex items-center gap-1.5 rounded-lg border border-rose-200 bg-white px-3 py-2 text-sm font-medium text-rose-700 hover:bg-rose-50 hover:border-rose-300 transition-colors dark:border-[#7f1d1d] dark:text-[#fca5a5] dark:hover:bg-[#3f1d1b]"
              >
                <X className="w-4 h-4" />
                <span className="hidden sm:inline">{t("listings.filter.reset")}</span>
              </button>
            )}
            {/* Phones keep one small line under the chips: the count, the
                reset (the page's one clear-all), the bell, the saved
                filters, the map and the sort (2026-10-29). */}
            {/* Save / re-apply the current filters as a named preset. */}
            <SavedFilters searchParams={searchParams} onApply={commitFilters} iconOnly />
            {/* Turn the current filters into an alert (disabled when an
                identical alert already exists). */}
            <CreateAlertButton searchParams={searchParams} iconOnly />
            {/* Jump to the gated map's Listings layer with the same filters. */}
            <Link
              href={mapHref}
              className="inline-flex items-center gap-1.5 rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm font-medium text-gray-700 hover:border-gray-300 transition-colors"
            >
              <MapIcon className="w-4 h-4" />
              <span className="hidden sm:inline">{t("listings.viewonmap")}</span>
            </Link>
            {/* Sorter sits at the far right of the controls row. */}
            <ListingsSortPicker value={sort} onChange={(v) => setParam("sort", v)} noValuation={isSeparate(searchParams.get("category"))} withLook={!!searchParams.get("photo")} />
          </div>
        </div>
        )}

        {/* Results (full width) */}
        <div className="min-w-0">
          {/* Grid fetches data and reports back total + pages. On the
              default first page it renders from the SSR-seeded
              initialData so the crawler sees real cards. */}
          <ListingsGrid
            searchParams={searchParams}
            page={page}
            initialData={initialData}
            view={view}
            seedParams={urlParams}
            seedKey={seedKey}
            onFilter={mergeParams}
            onTotalChange={(total, pages) => setPaginationInfo({ total, pages })}
            hidePlace={landing?.placeName ?? null}
            infinite
            openRequest={openRequest}
          />

          {/* No numbered pagination: pages append as the reader scrolls
              (the grid's infinite mode). */}
        </div>

        {landing?.footer}

        {/* Advanced search modal (opened from the filter bar's "Filtrlər") */}
        <ListingsFilterPanel
          open={advancedOpen}
          onClose={() => setAdvancedOpen(false)}
          searchParams={searchParams}
          onSearch={commitFilters}
          onReset={resetAll}
        />
      </div>
    </div>
  );
}

type ShellProps = Omit<Parameters<typeof ListingsShell>[0], "urlParams">;

function UrlShell(props: ShellProps) {
  return <ListingsShell {...props} urlParams={useSearchParams()} />;
}

const NO_PARAMS = new URLSearchParams() as unknown as ReadonlyURLSearchParams;

/** A landing page stands for one search, so its server HTML must not
 *  depend on the query string: that keeps it statically renderable (ISR)
 *  instead of falling back to a client render at a Suspense boundary.
 *  Anything in the URL (?q=, tracking tags) is applied after hydration. */
function LandingShell(props: ShellProps) {
  const [urlParams, setUrlParams] = useState(NO_PARAMS);
  useEffect(() => {
    const sp = new URLSearchParams(window.location.search);
    if (sp.toString()) setUrlParams(sp as unknown as ReadonlyURLSearchParams);
  }, []);
  return <ListingsShell {...props} urlParams={urlParams} />;
}

/**
 * Client entry point the server `page.tsx` renders. On /listings it wraps
 * the shell in a Suspense boundary (required around useSearchParams); a
 * landing page renders it directly. Threads the SSR-fetched first page
 * through as `initialData`.
 */
export function ListingsContent({
  initialData,
  landing,
  seedKey,
  intro,
}: {
  initialData?: ListingsResponse | null;
  landing?: ListingsLanding;
  seedKey?: string;
  /** /listings only: the server-rendered header (ListingsIntro). */
  intro?: ReactNode;
}) {
  if (landing) return <LandingShell initialData={initialData} landing={landing} seedKey={seedKey} intro={intro} />;
  return (
    <Suspense fallback={null}>
      <UrlShell initialData={initialData} seedKey={seedKey} intro={intro} />
    </Suspense>
  );
}
